import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Small uppercase, letter-spaced label with a leading hairline rule — the
 * editorial "eyebrow" that sits above a section headline. Adapted from the
 * internal deck's `.section-label` pattern (11px, 0.2em tracking, 28px rule).
 *
 * The `accent` prop colour-codes the branch: amber = Enterprise, teal =
 * Growth, neutral = muted grey (theme-aware). Light theme is the default.
 */

type Accent = 'enterprise' | 'growth' | 'neutral';

const accentText: Record<Accent, string> = {
  enterprise: 'text-enterprise-dark dark:text-enterprise',
  growth: 'text-growth-dark dark:text-growth',
  neutral: 'text-slate-500 dark:text-slate-400',
};

const accentRule: Record<Accent, string> = {
  enterprise: 'bg-enterprise-dark dark:bg-enterprise',
  growth: 'bg-growth-dark dark:bg-growth',
  neutral: 'bg-slate-300 dark:bg-slate-600',
};

export interface EyebrowLabelProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  accent?: Accent;
  /** Centre the label + rule (for centred section headers). */
  centered?: boolean;
}

export function EyebrowLabel({
  accent = 'neutral',
  centered = false,
  className,
  children,
  ...props
}: EyebrowLabelProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.2em]',
        centered && 'justify-center',
        accentText[accent],
        className,
      )}
      {...props}
    >
      <span className={cn('h-px w-7 shrink-0', accentRule[accent])} aria-hidden="true" />
      {children}
    </span>
  );
}

export default EyebrowLabel;
